/**
 * Analyzer contains business-logic for all schedulable tasks:
 * - Load mapper configuration for some advertiser
 * - Extract data based on mapper configuration
 * - Derive mapping settings based on DOM element
 * - Upload product information back to adacado
 */

import Common from './common'
import Logger from './logger'
import UploadInventoryRunner from './uploadInventoryRunner'

const WINDOW_NAMESPACE = 'adavada'

const defaultMapper = () => {
  return { css: {}, og: {} }
}

const defaultClientSettings = () => {
  return { traceProbability: 1 }
}

var config = {
  document: window.document,
  advertiserId: null,
  mapper: defaultMapper(),
  client: defaultClientSettings(),
  bootstrap: false,
  vertical: null,
  verticalFields: null,
}

var item = {
  product: null,
  css: {
    product: null,
  },
  og: {
    product: null,
  },
}

function setConfig({ target, value, quiet = false, dryRun = false }) {
  Logger.info(
    `analyzer.js - setConfig - set config with target=${target},value=${_truncate(
      JSON.stringify(value)
    )}`
  )
  let result = false

  // mapper is the only exception that can be unset to a default state
  if (value === null && target === 'mapper') {
    value = defaultMapper()
  }

  if (value === undefined || value === null) {
    return result
  }

  // Check if the target is one of the allowed configuration field:
  Logger.info(`analyzer.js - setConfig - checking target against allowed. target: ${target}`)
  if (
    ![
      'advertiserId',
      'bootstrap',
      'document',
      'mapper',
      'version',
      'vertical',
      'verticalFields',
    ].includes(target)
  ) {
    return result
  }

  if (target === 'mapper') {
    // Create all required fields before saving the given mapper object
    value = value || {}
    const original = config[target]

    if (!value.hasOwnProperty('css')) {
      value.css = (original && original.css) || {}
    }

    if (!value.hasOwnProperty('og')) {
      value.og = (original && original.og) || {}
    }
  }

  if (target === 'verticalFields') {
    value = _parseVerticalFields(value)
  }

  if (!dryRun) {
    config[target] = value
  }
  result = true

  if (quiet || dryRun) {
    return result
  }

  // Trigger update to product item if one of the following properties have been updated:
  Logger.info(`analyzer.js - setConfig - checking target against updated 1. target: ${target}`)
  if (['document', 'advertiserId', 'mapper'].includes(target)) {
    _invalidateItem()
  }

  // Trigger scrapping if both of following properties are updated
  Logger.info(`analyzer.js - setConfig - checking target against updated 2. target: ${target}`)
  if (['advertiserId', 'bootstrap'].includes(target)) {
    _invalidateRemoteInventory()
  }

  return result
}

function getConfig({ target }) {
  if (target === undefined) {
    Logger.info(`analyzer.js - getConfig - get all config`)
    return {
      ...config,
      // Remove document since this might result cross-domain violation for certain situation
      document: null,
    }
  }

  const result = config[target]
  Logger.info(
    `analyzer.js - getConfig - get config with target=${target},result=${JSON.stringify(result)}`
  )
  return result
}

function getElement({ target }) {
  const result = getCssElement({ target }) || getOgElement({ target }) || null
  Logger.info(
    `analyzer.js - getElement - get element with target=${target},result=${JSON.stringify(result)}`
  )
  return result
}

function setCssElement({ target, value, dryRun = false }) {
  Logger.info(
    `analyzer.js - setCssElement - set css::element with target=${target},value=${_stringifyInnerHTMLOrSrcOrNull(
      value
    )}`
  )
  let result = false

  if (!target) {
    return result
  }

  if (value === null) {
    if (!dryRun) {
      delete config.mapper.css[target]
    }
    result = true
    return result
  }

  if (!value || !_isElement(value)) {
    return result
  }

  const stoppingElementTags = ['html', 'body']
  const crawled = []

  // crawl through the parents until reaching either html or body tags
  let search = value
  while (search !== null && !stoppingElementTags.includes(search.tagName.toLowerCase())) {
    // record id's, tag-names and class-names along the way
    crawled.push({
      search,
      id: search.id,
      tagName: _lowerCaseOrNull(search.tagName),
      classNames: (search.className || '')
        .split(' ')
        .map(s => s.trim())
        .filter(s => s.length > 0)
        // we need to black-list any classes that contain "adacado"
        // since those might be injected by the mapper application
        .filter(s => !/adacado/i.test(s))
        // also assume any class-name containing at least 3 digits
        // are temporary, and cannot be used as selectors
        .filter(s => !/\d{3}/.test(s)),
    })

    search = search.parentElement
  }

  if (crawled.length === 0) {
    return result
  }

  // discard elements until we find a node contining "id" attribute (from child to parent)
  // then order the collection from parent to child
  const legitElements = _filterUntil(crawled, item => !!item.id).reverse()

  // compute the prefer local-selector
  for (let i = 0; i < legitElements.length; i++) {
    const item = legitElements[i]

    let idSelector, classSelector

    // Id is preferred selector when present
    if (item.id) {
      idSelector = '#' + item.id
    }

    if (item.classNames && item.classNames.length > 0) {
      classSelector = item.classNames.map(s => '.' + s).join('')
    }

    item.idSelector = idSelector
    item.classSelector = classSelector

    item.localSelector = idSelector || classSelector || item.tagName
  }

  let cssSelector = legitElements
    .reduce((parentSelector, item) => {
      // return the preferred selector for the first item
      if (parentSelector === '') {
        return item.localSelector
      }

      // Attempt to limit the local-selector by nth-child pseudo class
      //
      // Note it is possible that the local-selector becomes out-dated
      // when we perform this search (very rare), so fallback to tag-name
      // for that case
      const enhancedLocalSelector =
        _withNthChildClass(parentSelector, config.document, item) || item.tagName

      return parentSelector + ' > ' + enhancedLocalSelector
    }, '')
    .trim()

  if (!cssSelector || cssSelector.length === 0) {
    return result
  }

  var finalConfirm = Common.attempt(s => config.document.querySelectorAll(s), cssSelector)

  if (finalConfirm && finalConfirm.length > 0 && finalConfirm.item(0) === value) {
    Logger.info(
      `analyzer.js - setCssElement - set mapper::css::${target} with cssSelector=${cssSelector}`
    )
    if (!dryRun) {
      config.mapper.css[target] = cssSelector
    }
    result = true
  } else {
    Logger.all(
      `[ERROR] cannot retrieve the original element with generated cssSelector of "${cssSelector}"`
    )
  }

  return result
}

function _withNthChildClass(parentSelector, theDocument, element) {
  const { localSelector, search } = element

  let currentSelector = localSelector

  if (currentSelector === element.idSelector) {
    return currentSelector
  }

  let found = false
  try {
    let searchSelector = parentSelector + ' > ' + currentSelector

    let confirmResult = theDocument.querySelectorAll(searchSelector)

    if (!confirmResult || confirmResult.length === 0) {
      throw 'cannot find the required element with ' + searchSelector
    }

    let foundIndex = -1
    for (let i = 0; i < confirmResult.length; i++) {
      if (confirmResult.item(i) === search) {
        found = true
        foundIndex = i
        break
      }
    }

    if (!found) {
      throw 'cannot find the required element'
    }

    if (confirmResult.length === 1) {
      throw 'found only one result, do not require nth-of-type pseudo class'
    }

    // we cannot use nth-of-type for pure class-selector
    if (currentSelector === element.classSelector) {
      if (foundIndex === 0) {
        return currentSelector
      } else {
        // downgrade the selector to tag-name-selector
        element.localSelector = element.tagName
        return _withNthChildClass(parentSelector, theDocument, element)
      }
    }

    // else we are dealing with tag-selector...

    // add nth-of-type even if the desired element is the first of result
    currentSelector += `:nth-of-type(${foundIndex + 1})`
  } catch (err) {
    Logger.warn(`analyzer.js - _withNthChildClass - error: ${err}`)
  }

  return found ? currentSelector : false
}

function getCssElement({ target }) {
  const cssSelector = config.mapper.css[target]

  if (!cssSelector) {
    return null
  }

  // first attempt with the complete selector
  let resultList = Common.attempt(s => config.document.querySelectorAll(s), cssSelector)

  if (!resultList || resultList.length === 0) {
    // second attempt with the first two levels chopped off
    resultList = Common.attempt(
      s => config.document.querySelectorAll(s),
      _withLevelSkipped(cssSelector, 2)
    )
  }

  if (!resultList || resultList.length === 0) {
    Logger.warn(`cannot find any css::element with selector=${cssSelector}`)
  } else if (resultList.length > 1) {
    Logger.warn(
      `multiple css::element with selector=${cssSelector} were found. (count = ${resultList.length})`
    )
  }

  const result = resultList && resultList.length > 0 ? resultList.item(0) : null
  Logger.info(
    `analyzer.js - getCssElement - get css::element with target=${target},result=${Common.extractValue(
      result,
      _toValidationType(target, config.verticalFields),
      config.document
    )}`
  )
  return result
}

function setOgElement({ target, value, dryRun = false }) {
  Logger.info(
    `analyzer.js - setOgElement - set og::element with target=${target},value=${_stringifyInnerHTMLOrSrcOrNull(
      value
    )}`
  )
  let result = false

  if (!target) {
    return result
  }

  if (value === null) {
    if (!dryRun) {
      delete config.mapper.og[target]
    }
    result = true
    return result
  }

  if (!value || !_isElement(value) || !/^meta$/i.test(value.tagName)) {
    return result
  }

  const ogSelector = value.getAttribute('property')

  if (/^og:/i.test(ogSelector)) {
    Logger.info(
      `analyzer.js - setOgElement - set mapper::og::${target} with ogSelector=${ogSelector}`
    )
    if (!dryRun) {
      config.mapper.og[target] = ogSelector
    }
    result = true
  }

  return result
}

function getOgElement({ target }) {
  const ogSelector = config.mapper.og[target]

  if (!ogSelector) {
    return null
  }

  const resultList = Common.attempt(
    s => config.document.querySelectorAll(s),
    `meta[property="${ogSelector}"]`
  )

  if (!resultList || resultList.length === 0) {
    Logger.warn(`cannot find any og::element with selector=${ogSelector}`)
  } else if (resultList.length > 1) {
    Logger.warn(
      `multiple og::element with selector=${ogSelector} were found. (count = ${resultList.length})`
    )
  }

  const result = resultList && resultList.length > 0 ? resultList.item(0) : null
  Logger.info(
    `analyzer.js - setOgElement - get css::element with target=${target},result=${Common.extractValue(
      result,
      _toValidationType(target, config.verticalFields),
      config.document
    )}`
  )

  return result
}

function getItem({ target }) {
  var result = null
  if (target === 'product') {
    _invalidateItem()
    result = item.product
  }
  Logger.info(
    `analyzer.js - getItem - get item with target=${target},result=${JSON.stringify(result)}`
  )
  return result
}

function getCssItem({ target }) {
  var result = null
  if (target === 'product') {
    _invalidateItem()
    result = item.css.product
  }
  Logger.info(
    `analyzer.js - getCssItem - get css::item with target=${target},result=${JSON.stringify(
      result
    )}`
  )
  return result
}

function getOgItem({ target }) {
  var result = null
  if (target === 'product') {
    _invalidateItem()
    result = item.og.product
  }
  Logger.info(
    `analyzer.js - getOgItem - get og::item with target=${target},result=${JSON.stringify(result)}`
  )
  return result
}

function _invalidateItem() {
  let newCssProduct = {}
  let newOgProduct = {}

  for (let target of Object.keys(config.mapper.css)) {
    if (!_isMappable(target, config.verticalFields)) {
      continue
    }

    let value = Common.extractValue(
      getCssElement({ target }),
      _toValidationType(target, config.verticalFields),
      config.document
    )
    if (value || value === 0) {
      newCssProduct[target] = value
    }
  }

  for (let target of Object.keys(config.mapper.og)) {
    if (!_isMappable(target, config.verticalFields)) {
      continue
    }
    let value = Common.extractValue(
      getOgElement({ target }),
      _toValidationType(target, config.verticalFields),
      config.document
    )
    if (value || value === 0) {
      newOgProduct[target] = value
    }
  }

  // Add product-link when we are able to capture something
  // from existing mappings
  let newProduct = null
  if (
    config.document.URL &&
    (Object.keys(newCssProduct).length > 0 || Object.keys(newOgProduct).length > 0)
  ) {
    newProduct = {}

    newProduct.productLink = config.document.URL

    const isLocalLink = /^https?:\/\/(localhost|127\.0\.0)/.test(config.document.URL)
    const isAdacado = /adacado\.com/.test(config.document.URL)

    // Back-fill the productId using productLink
    if (!isLocalLink && !isAdacado && !newProduct.productId) {
      newProduct.productId = Common.generateIdByURL(newProduct.productLink)
    }

    // CSS values are preferred over Open-Graph, and those two overwrite anything in the default
    Object.assign(newProduct, newOgProduct, newCssProduct)
  }

  item.product = newProduct
  item.css.product = newCssProduct
  item.og.product = newOgProduct
}

function _parseVerticalFields(rawConfig) {
  if (!rawConfig || rawConfig.length === 0) {
    return null
  }

  return rawConfig
    .filter(field => {
      return (
        field &&
        field.fieldName &&
        field.validationType &&
        field.scrapermapper &&
        field.scrapermapper.isMappable
      )
    })
    .reduce((acc, cur) => {
      acc[cur.fieldName] = {
        validationType: cur.validationType,
      }
      return acc
    }, {})
}

function _isMappable(targetField, verticalFields) {
  if (!verticalFields) {
    // allow any field to be mappable when vertical-fields are not available
    return true
  }

  return verticalFields.hasOwnProperty(targetField)
}

function _toValidationType(targetField, verticalFields) {
  if (!verticalFields) {
    return targetField
  }

  return verticalFields.hasOwnProperty(targetField)
    ? verticalFields[targetField].validationType
    : 'text'
}

function _isElement(o) {
  return typeof HTMLElement === 'object'
    ? o instanceof HTMLElement //DOM2
    : o && typeof o === 'object' && o !== null && o.nodeType === 1 && typeof o.nodeName === 'string'
}

function _lowerCaseOrNull(o) {
  return o && typeof o.toLowerCase === 'function' ? o.toLowerCase() : null
}

function _stringifyInnerHTMLOrSrcOrNull(o) {
  if (!_isElement(o)) {
    return null
  }

  if (/^img$/i.test(o.tagName)) {
    return o.getAttribute('src')
  } else if (/^meta$/i.test(o.tagName)) {
    return o.getAttribute('content')
  } else {
    return JSON.stringify(o.innerHTML)
  }
}

function _truncate(input, maxLength = 500) {
  return !input || typeof input !== 'string' || input.length <= maxLength
    ? input
    : input.substring(0, maxLength) + '...'
}

function _filterUntil(source, predicate) {
  if (!source || source.length === 0) {
    return []
  }

  let shouldStop = false

  return source.filter(i => {
    if (shouldStop) {
      return false
    }
    if (predicate(i)) {
      shouldStop = true
    }
    return true
  })
}

/**
 * Remove top level CSS selectors with the indicated value.
 *
 * If the indicated value is equal or larger than the available levels,
 * then return the original selector input.
 *
 * @param {String} cssSelector a String containing one or more selectors to match
 * @param {Integer} numOfLevels number of level to be skipped from the top
 */
function _withLevelSkipped(cssSelector, numOfLevels) {
  if (!cssSelector || !numOfLevels || numOfLevels < 0) {
    return cssSelector
  }

  const levels = cssSelector.split(' ')

  if (levels.length <= numOfLevels) {
    // return the original selector if we cannot skip the indicated levels
    return cssSelector
  }

  let count = 0
  return levels.filter(() => ++count > numOfLevels).join(' ')
}

async function _invalidateRemoteInventory() {
  const { advertiserId, bootstrap, client, document, vertical: verticalFallback } = config

  if (!advertiserId || !bootstrap) {
    return
  }

  UploadInventoryRunner.run(
    {
      advertiserId,
      client,
      document,
      verticalFallback,
    },
    { setConfig, getItem }
  )
}

export default {
  WINDOW_NAMESPACE,
  setConfig,
  getConfig,
  getElement,
  getOgElement,
  setOgElement,
  getCssElement,
  setCssElement,
  getItem,
  getCssItem,
  getOgItem,
}
